<template>
    <div class="mb-12">
        <div class="pt-12 text-brand-dark text-4xl font">My Calculations</div>
        <div v-if="matrices.length > 0">
            <div class="flex justify-end py-4 h-18">
                <div
                    v-show="checkedRows.length > 0"
                    class="relative btn bg-grey-dark text-grey-lightest cursor-pointer "
                    @click="selectedActionableRecords = checkedRows; showDeleteModal = true;"
                >
                    Delete Selected
                    <div
                        class="text-sm border border-solid border-white rounded-full bg-grey-dark flex-none h-6 w-6 flex items-center justify-center absolute"
                        style="right: -0.75rem;top: -0.75rem;"
                    >{{ checkedRows.length }}</div>
                </div>
            </div>
            <div class="shadow-lg">
                <b-table
                    :data="matrices"
                    hoverable
                    striped
                    :checked-rows.sync="checkedRows"
                    checkable
                    detailed
                    detail-key="id"
                    :has-detailed-visible="() => true"
                    icon-pack="fas"
                >
                    <template slot-scope="props">
                        <b-table-column
                            field="name"
                            label="Name"
                        >
                            {{ props.row.name }}
                        </b-table-column>
                        <b-table-column
                            field="diet.name"
                            label="Diet"
                        >
                            {{ props.row.diet ? props.row.diet.name : '' }}
                        </b-table-column>
                        <b-table-column
                            field="productGroup"
                            label="Product Group"
                        >
                            {{ props.row.productGroup }}
                        </b-table-column>
                        <b-table-column
                            field="productForm"
                            label="Product Form"
                        >
                            {{ props.row.productForm }}
                        </b-table-column>
                        <b-table-column
                            field="dietaryPhytate"
                            label="Dietary Phytate"
                        >
                            {{ props.row.dietaryPhytate }}
                        </b-table-column>
                        <b-table-column
                            field="dose"
                            label="Dose"
                        >
                            {{ props.row.dose }}
                        </b-table-column>
                        <b-table-column custom-key="actions">
                            <div class="flex justify-around">
                                <a :href="`/home/${props.row.id}`">
                                    <b-icon
                                        icon="eye"
                                        size="is-small"
                                        custom-class="text-grey-dark"
                                    ></b-icon>
                                </a>
                                <div
                                    class="cursor-pointer ml-4"
                                    @click="selectedActionableRecords = [props.row]; showDeleteModal = true"
                                >
                                    <b-icon
                                        icon="trash-alt"
                                        size="is-small"
                                        custom-class="text-grey-dark"
                                    ></b-icon>
                                </div>
                            </div>
                        </b-table-column>
                    </template>
                    <template
                        slot="detail"
                        slot-scope="props"
                    >
                        <b-table
                            :data="props.row.nutrients"
                            :columns="nutrientColumns"
                            :narrowed="true"
                        ></b-table>
                    </template>
                </b-table>
            </div>
        </div>
        <div v-else>
            <div class="w-full my-8 bg-container shadow-lg rounded flex justify-center">
                <div class="w-full md:w-2/5 flex flex-col text-center justify-center align-center">
                    <icon-empty class="p-4 h-48 sm:h-48 md:h-64 lg:h-80" />
                    <div class="text-grey text-base md:text-xl lg:text-2xl font-light">You don't have any saved matrix calculations yet!</div>
                    <div class="text-grey-light text-sm md:text-base lg:text-xl">What are you waiting for?</div>
                    <div class="flex justify-center">
                        <a
                            href="/"
                            class="shadow btn-large btn-green mx-2 my-6 inline-flex items-center"
                        >
                            Start new Matrix Calculation
                        </a>
                    </div>
                </div>
            </div>
        </div>
        <custom-modal
            :showModal="showDeleteModal"
            :title="`Delete Calculation${checkedRows.length > 1 ? 's' : ''}?`"
            @update:showModal="data => showDeleteModal = data"
            :submitBtn="{text:'Delete', type:'danger'}"
            @submit="() => {showDeleteModal = false; deleteRecords()}"
        >
            <div slot="body">
                <p>Are you sure you want to delete the selected record{{checkedRows.length > 1 ? 's' : ''}}?</p>
            </div>
        </custom-modal>
    </div>
</template>

<script>
import IconEmpty from "../components/icons/IconEmpty.vue";
import { bugsnagClient } from "../lib/bugsnag";

export default {
    components: {
        IconEmpty
    },
    props: ["matrixData"],
    data() {
        return {
            columns: [
                {
                    field: "name",
                    label: "Name",
                    sortable: true
                },
                {
                    field: "diet.name",
                    label: "Diet"
                },
                {
                    field: "productGroup",
                    label: "Product Group"
                },
                {
                    field: "productForm",
                    label: "Product Form"
                },
                {
                    field: "dietaryPhytate",
                    label: "Dietary Phytate",
                    isNumeric: true
                },
                {
                    field: "dose",
                    label: "Dose",
                    isNumeric: true
                }
            ],
            nutrientColumns: [
                {
                    field: "name",
                    label: "Name"
                },
                {
                    field: "pivot.unit",
                    label: "Unit"
                },
                {
                    field: "contribution",
                    label: "Contribution"
                },
                {
                    field: "value",
                    label: "Matrix Value"
                }
            ],
            checkedRows: [],
            showDeleteModal: false,
            selectedActionableRecords: [],
            updatedData: null
        };
    },
    mounted() {},
    computed: {
        matrices: function() {
            return this.updatedData != null
                ? this.updatedData
                : JSON.parse(this.matrixData).map(matrix => {
                      return {
                          ...matrix,
                          nutrients: matrix.nutrients.map(nutrient => {
                              return {
                                  ...nutrient,
                                  contribution: _.round(
                                      nutrient.pivot.contribution,
                                      3
                                  ),
                                  value: _.round(nutrient.pivot.value, 3)
                              };
                          })
                      };
                  });
        },

        recordIdsToBeDeleted: function() {
            return this.selectedActionableRecords.map(row => row.id);
        }
    },
    methods: {
        handleModalSubmit() {
            this.showDeleteModal = false;
        },

        deleteRecords() {
            var self = this;
            axios
                .delete("/matrices/destroy", {
                    params: {
                        ids: self.recordIdsToBeDeleted
                    }
                })
                .then(response => {
                    self.$toasted.global.calc_success({
                        mainMessage:
                            self.selectedActionableRecords.length === 1
                                ? `Calculation '${
                                      self.selectedActionableRecords[0].name
                                  }' deleted successfully!`
                                : `${
                                      self.selectedActionableRecords.length
                                  } Calculations deleted successfully!`
                    });
                    //Remove deleted record from array
                    self.updatedData = self.matrices.filter(
                        matrix => !self.recordIdsToBeDeleted.includes(matrix.id)
                    );
                    self.selectedActionableRecords = [];
                })
                .catch(error => {
                    self.$toasted.global.calc_error({
                        error
                    });
                    bugsnagClient.notify(error);
                });
        }
    }
};
</script>

<style>
</style>
